import { useEffect, useState } from "react";
import { recordSectionEvent } from "@/lib/api";

/* Marks each tracked section id as "seen" the first time ≥35% enters the
 * viewport, and measures dwell time per section, flushing it to the backend
 * every 15s (and on tab hide) for the admin section heatmap.
 * Pass a module-level constant array so the effect runs once. */
export function useSectionTracking(sectionIds) {
  const [seenSections, setSeenSections] = useState(() => new Set());

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const visibleSince = new Map(); // id -> ms timestamp
    const pending = new Map();      // id -> accumulated seconds

    const fold = () => {
      const now = Date.now();
      for (const [id, start] of visibleSince) {
        pending.set(id, (pending.get(id) || 0) + (now - start) / 1000);
        visibleSince.set(id, now);
      }
    };
    const flush = () => {
      fold();
      for (const [id, sec] of pending) {
        const s = Math.round(sec);
        if (s >= 1) recordSectionEvent(id, s);
      }
      pending.clear();
    };

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const id = entry.target.id;
        if (!id) continue;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
          setSeenSections((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
          if (!visibleSince.has(id)) visibleSince.set(id, Date.now());
        } else if (visibleSince.has(id)) {
          pending.set(id, (pending.get(id) || 0) + (Date.now() - visibleSince.get(id)) / 1000);
          visibleSince.delete(id);
        }
      }
    }, { threshold: [0.35] });
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    const interval = setInterval(flush, 15000);
    const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flush);
    return () => {
      observer.disconnect();
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [sectionIds]);

  const engagementPct = Math.round((seenSections.size / sectionIds.length) * 100);
  return { seenSections, engagementPct };
}
